import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchProducts, fetchCollection } from "@/lib/shopify";
import ProductCard from "@/components/product/ProductCard";

const FALLBACK_COL = {
  all: { title: "The Edit", subtitle: "Every piece, in one considered place", hero: null },
  dresses: { title: "Dresses", subtitle: "Silhouettes that move with you", hero: null },
  "new-arrivals": { title: "New Arrivals", subtitle: "This week, freshly landed", hero: null },
  sale: { title: "Sale", subtitle: "Considered pieces, gently priced", hero: null },
};

const SORT_OPTIONS = [
  { value: "featured", label: "Featured" },
  { value: "price-asc", label: "Price: Low to High" },
  { value: "price-desc", label: "Price: High to Low" },
  { value: "title-asc", label: "A – Z" },
];

const Shop = ({ collection: collectionProp }) => {
  const params = useParams();
  const collection = collectionProp || params.handle || "all";
  const [sort, setSort] = useState("featured");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);
  const [col, setCol] = useState(FALLBACK_COL[collection] || FALLBACK_COL.all);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        if (collection === "all") {
          const list = await fetchProducts();
          if (!alive) return;
          setProducts(list);
          setCol(FALLBACK_COL.all);
        } else {
          const { collection: colMeta, products: list } = await fetchCollection(collection);
          if (!alive) return;
          setProducts(list || []);
          setCol({
            title: colMeta?.title || FALLBACK_COL[collection]?.title || collection,
            subtitle: colMeta?.subtitle || FALLBACK_COL[collection]?.subtitle || "",
            hero: colMeta?.hero || null,
          });
        }
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Could not load products.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [collection]);

  const sortedProducts = useMemo(() => {
    const list = [...products];
    switch (sort) {
      case "price-asc": return list.sort((a, b) => (a.price || 0) - (b.price || 0));
      case "price-desc": return list.sort((a, b) => (b.price || 0) - (a.price || 0));
      case "title-asc": return list.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
      default: return list;
    }
  }, [products, sort]);

  return (
    <div data-testid={`shop-${collection}`}>
      {/* Collection hero */}
      <section className="relative h-[45vh] md:h-[55vh] w-full overflow-hidden bg-[color:var(--vw-bg-alt)]">
        {col.hero && <img src={col.hero} alt={col.title} className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-black/25" />
        <div className="relative h-full flex flex-col items-center justify-center text-white text-center px-6">
          <p className="overline opacity-90 animate-fade-up">The Collection</p>
          <h1 className="font-serif-display text-5xl md:text-7xl italic mt-3 animate-fade-up delay-100">{col.title}</h1>
          {col.subtitle && (
            <p className="mt-3 text-sm md:text-base opacity-90 max-w-md animate-fade-up delay-200">{col.subtitle}</p>
          )}
        </div>
      </section>

      {/* Toolbar */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-10 flex items-center justify-between">
        <p className="overline text-[color:var(--vw-ink-soft)]">
          {loading ? "Loading…" : `${sortedProducts.length} pieces`}
        </p>
        <label className="flex items-center gap-3">
          <span className="overline text-[color:var(--vw-ink-soft)]">Sort</span>
          <select
            data-testid="sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-transparent border-b border-[color:var(--vw-ink)] pb-1 font-utility text-sm outline-none cursor-pointer pr-6"
          >
            {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
      </div>

      {/* Grid */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-16">
        {error ? (
          <p data-testid="shop-error" className="text-center py-20 text-[color:var(--vw-ink-soft)]">{error}</p>
        ) : loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[4/5] bg-[color:var(--vw-bg-alt)]" />
                <div className="h-4 bg-[color:var(--vw-bg-alt)] mt-4 w-2/3" />
                <div className="h-3 bg-[color:var(--vw-bg-alt)] mt-2 w-1/3" />
              </div>
            ))}
          </div>
        ) : sortedProducts.length === 0 ? (
          <p data-testid="shop-empty" className="font-serif-display text-3xl italic text-center py-20">Nothing here yet — check back soon.</p>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
            {sortedProducts.map((p, i) => <ProductCard key={p.id || p.handle} product={p} index={i} />)}
          </div>
        )}
      </section>
    </div>
  );
};

export default Shop;
